import React, { useState, useEffect, useRef } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import AREngine from 'services/arEngine';
import ObjectDetectionService from 'services/objectDetection';
import ARFinancialService from 'services/arFinancialService';
import QRCodeScanner from 'services/qrCodeScanner';
import toast from 'react-hot-toast';

const ARModeViewer = ({ budgetData, financialData, onClose, userRole = 'retail' }) => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [arActive, setArActive] = useState(false);
  const [detectedObject, setDetectedObject] = useState(null);
  const [financialImpact, setFinancialImpact] = useState(null);
  const [behavioralAnalysis, setBehavioralAnalysis] = useState(null);
  const [scenarioSimulation, setScenarioSimulation] = useState(null);
  const [showSimulation, setShowSimulation] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [manualProduct, setManualProduct] = useState({ name: '', cost: '', category: 'Shopping' });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [qrScanEnabled, setQrScanEnabled] = useState(false);
  const [qrDetected, setQrDetected] = useState(null);
  const [isQrScanning, setIsQrScanning] = useState(false);
  const [qrScanStatus, setQrScanStatus] = useState(null);

  const containerRef = useRef(null);
  const arEngineRef = useRef(null);
  const detectionServiceRef = useRef(null);
  const financialServiceRef = useRef(null);
  const detectionIntervalRef = useRef(null);
  const qrScannerRef = useRef(null);
  const qrScanIntervalRef = useRef(null);

  useEffect(() => {
    initializeAR();
    return () => {
      cleanup();
    };
  }, []);

  const initializeAR = async () => {
    try {
      setIsInitializing(true);

      // Initialize services
      arEngineRef.current = new AREngine();
      detectionServiceRef.current = new ObjectDetectionService();
      financialServiceRef.current = new ARFinancialService();
      qrScannerRef.current = new QRCodeScanner();

      // Set budget data
      if (budgetData) {
        financialServiceRef?.current?.setBudgetData(budgetData);
      }

      // Initialize AR engine
      const engineResult = await arEngineRef?.current?.initialize(containerRef?.current);
      if (!engineResult?.success) {
        throw new Error(engineResult.error);
      }

      // Start camera
      const cameraResult = await arEngineRef?.current?.startCameraAccess();
      if (!cameraResult?.success) {
        throw new Error(cameraResult.error);
      }

      // Load detection model
      await detectionServiceRef?.current?.loadModel();

      // Start render loop
      arEngineRef?.current?.startRenderLoop();

      setArActive(true);
      setIsInitializing(false);
      toast?.success('AR Mode activated');

      // Start continuous object detection
      startObjectDetection();
    } catch (error) {
      console.error('AR initialization error:', error);
      toast?.error(`Failed to start AR: ${error?.message}`);
      setIsInitializing(false);
    }
  };

  const startObjectDetection = () => {
    if (detectionIntervalRef?.current) return;

    detectionIntervalRef.current = setInterval(async () => {
      if (!arEngineRef?.current?.videoElement || !detectionServiceRef?.current) return;

      const result = await detectionServiceRef?.current?.detectFromVideo(
        arEngineRef?.current?.videoElement
      );

      if (result?.success && result?.objects?.length > 0) {
        const primaryObject = result?.objects?.[0];
        handleObjectDetected(primaryObject);
      }
    }, 2000); // Detect every 2 seconds
  };

  const stopObjectDetection = () => {
    if (detectionIntervalRef?.current) {
      clearInterval(detectionIntervalRef?.current);
      detectionIntervalRef.current = null;
    }
  };

  const toggleQRScanning = () => {
    if (qrScanEnabled) {
      stopQRScanning();
      setQrScanEnabled(false);
      toast?.success('QR scanning disabled');
      // Resume object detection
      startObjectDetection();
    } else {
      stopObjectDetection();
      startQRScanning();
      setQrScanEnabled(true);
      toast?.success('QR scanning enabled - Point camera at QR code');
    }
  };

  const startQRScanning = () => {
    if (!arEngineRef?.current?.videoElement || !qrScannerRef?.current) return;

    setIsQrScanning(true);
    setQrScanStatus('scanning');

    qrScanIntervalRef.current = qrScannerRef?.current?.startContinuousScanning(
      arEngineRef?.current?.videoElement,
      (result) => handleQRDetected(result), // Fixed: wrap in arrow function to avoid hoisting issue
      500
    );
  };

  const stopQRScanning = () => {
    if (qrScanIntervalRef?.current) {
      clearInterval(qrScanIntervalRef?.current);
      qrScanIntervalRef.current = null;
    }
    if (qrScannerRef?.current) {
      qrScannerRef?.current?.stopScanning();
    }
    setIsQrScanning(false);
    setQrScanStatus(null);
  };

  const handleQRDetected = async (result) => {
    try {
      setQrScanStatus('detected');
      setQrDetected(result?.qrCode);

      // Visual feedback
      toast?.success('QR Code detected! Loading product...', { duration: 2000 });

      // Lookup product information
      const productLookup = await qrScannerRef?.current?.lookupProduct(result?.qrCode);

      if (productLookup?.success) {
        const product = productLookup?.product;
        setQrScanStatus('success');

        // Create product object for financial analysis
        const productObject = {
          class: product?.name,
          category: product?.category,
          confidence: 100,
          source: 'qr_code'
        };

        setDetectedObject(productObject);

        // Calculate financial impact
        const impact = financialServiceRef?.current?.calculatePurchaseImpact(
          product?.price,
          product?.category
        );
        setFinancialImpact(impact);

        // Create AR overlay
        if (arEngineRef?.current) {
          arEngineRef?.current?.clearOverlays();
          arEngineRef?.current?.createOverlayPanel(
            { x: 0, y: 1, z: -3 },
            {
              title: product?.name,
              cost: product?.price,
              budget: impact?.categoryBudget,
              remaining: impact?.remainingBudget,
              impact: impact?.riskLevel,
              riskColor: impact?.riskColor
            }
          );
        }

        // Analyze behavioral triggers
        analyzeBehavior({
          productName: product?.name,
          estimatedCost: product?.price,
          category: product?.category,
          budgetPercentage: impact?.budgetPercentage,
          remainingBudget: impact?.remainingBudget
        });

        toast?.success(`${product?.name} - $${product?.price}`);

        // Reset QR scanner for next scan
        setTimeout(() => {
          qrScannerRef?.current?.reset();
          setQrScanStatus('scanning');
        }, 3000);
      } else {
        setQrScanStatus('error');
        toast?.error('Product not found in database');
        setTimeout(() => setQrScanStatus('scanning'), 2000);
      }
    } catch (error) {
      console.error('QR detection handling error:', error);
      setQrScanStatus('error');
      toast?.error('Failed to process QR code');
      setTimeout(() => setQrScanStatus('scanning'), 2000);
    }
  };

  const handleObjectDetected = async (object) => {
    setDetectedObject(object);

    // Estimate price
    const priceEstimate = detectionServiceRef?.current?.estimatePrice(
      object?.class,
      object?.category
    );

    // Calculate financial impact
    const impact = financialServiceRef?.current?.calculatePurchaseImpact(
      priceEstimate?.estimated,
      object?.category
    );
    setFinancialImpact(impact);

    // Create AR overlay
    if (arEngineRef?.current) {
      arEngineRef?.current?.clearOverlays();
      arEngineRef?.current?.createOverlayPanel(
        { x: 0, y: 1, z: -3 },
        {
          title: object?.class,
          cost: priceEstimate?.estimated,
          budget: impact?.categoryBudget,
          remaining: impact?.remainingBudget,
          impact: impact?.riskLevel,
          riskColor: impact?.riskColor
        }
      );
    }

    // Analyze behavioral triggers
    if (!isAnalyzing) {
      analyzeBehavior({
        productName: object?.class,
        estimatedCost: priceEstimate?.estimated,
        category: object?.category,
        budgetPercentage: impact?.budgetPercentage,
        remainingBudget: impact?.remainingBudget
      });
    }
  };

  const analyzeBehavior = async (purchaseContext) => {
    setIsAnalyzing(true);
    try {
      const analysis = await financialServiceRef?.current?.analyzeBehavioralTriggers(purchaseContext);
      setBehavioralAnalysis(analysis);
    } catch (error) {
      console.error('Behavioral analysis error:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSimulateImpact = () => {
    if (!financialImpact) return;

    const simulation = financialServiceRef?.current?.simulateFinancialScenario(
      financialImpact?.estimatedCost,
      financialImpact?.category
    );
    setScenarioSimulation(simulation);
    setShowSimulation(true);
  };

  const handleManualEntry = () => {
    stopObjectDetection();
    setManualEntry(true);
  };

  const handleManualSubmit = () => {
    if (!manualProduct?.name || !manualProduct?.cost) {
      toast?.error('Please enter product name and cost');
      return;
    }

    const cost = parseFloat(manualProduct?.cost);
    if (isNaN(cost) || cost <= 0) {
      toast?.error('Please enter a valid cost');
      return;
    }

    const mockObject = {
      class: manualProduct?.name,
      category: manualProduct?.category,
      confidence: 100
    };

    handleObjectDetected(mockObject);
    setManualEntry(false);
    startObjectDetection();
  };

  const cleanup = async () => {
    stopObjectDetection();
    stopQRScanning();
    if (arEngineRef?.current) {
      await arEngineRef?.current?.cleanup();
    }
    if (detectionServiceRef?.current) {
      detectionServiceRef?.current?.dispose();
    }
  };

  const getRiskColorClass = (riskLevel) => {
    switch (riskLevel) {
      case 'safe': return 'text-success border-success';
      case 'caution': return 'text-warning border-warning';
      case 'high': return 'text-warning border-warning';
      case 'critical': return 'text-error border-error';
      default: return 'text-muted border-muted';
    }
  };

  const getNudgeColorClass = (type) => {
    switch (type) {
      case 'warning': return 'bg-error/10 border-error text-error';
      case 'success': return 'bg-success/10 border-success text-success';
      case 'info': return 'bg-primary/10 border-primary text-primary';
      default: return 'bg-muted/10 border-muted text-muted';
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* AR Container */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* QR Scanning Frame Overlay */}
      {qrScanEnabled && (
        <div className="absolute inset-0 z-5 pointer-events-none">
          {/* Scanning frame */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className={`w-64 h-64 border-4 rounded-2xl transition-all duration-300 ${
              qrScanStatus === 'scanning' ? 'border-primary animate-pulse' :
              qrScanStatus === 'detected' ? 'border-warning' :
              qrScanStatus === 'success' ? 'border-success' :
              qrScanStatus === 'error'? 'border-error' : 'border-primary'
            }`}>
              {/* Corner markers */}
              <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-primary rounded-tl-lg" />
              <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-primary rounded-tr-lg" />
              <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-primary rounded-bl-lg" />
              <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-primary rounded-br-lg" />
              
              {/* Scanning line animation */}
              {qrScanStatus === 'scanning' && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-primary animate-scan" />
              )}
              
              {/* Status icon */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                {qrScanStatus === 'scanning' && (
                  <Icon name="QrCode" size={48} className="text-primary animate-pulse" />
                )}
                {qrScanStatus === 'success' && (
                  <Icon name="CheckCircle" size={48} className="text-success" />
                )}
                {qrScanStatus === 'error' && (
                  <Icon name="XCircle" size={48} className="text-error" />
                )}
              </div>
            </div>
            
            {/* Status text */}
            <div className="mt-4 text-center">
              <p className={`text-sm font-medium ${
                qrScanStatus === 'scanning' ? 'text-primary' :
                qrScanStatus === 'success' ? 'text-success' :
                qrScanStatus === 'error'? 'text-error' : 'text-white'
              }`}>
                {qrScanStatus === 'scanning' && 'Scanning for QR code...'}
                {qrScanStatus === 'detected' && 'QR Code detected!'}
                {qrScanStatus === 'success' && 'Product loaded successfully'}
                {qrScanStatus === 'error' && 'Failed to read QR code'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header Controls */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Icon name="Camera" size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-semibold">AR Financial Mode</h2>
              <p className="text-white/70 text-xs">
                {qrScanEnabled ? 'QR Scan Mode' : 'Point camera at products'}
              </p>
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
          >
            <Icon name="X" size={24} />
          </Button>
        </div>
      </div>
      {/* Loading Overlay */}
      {isInitializing && (
        <div className="absolute inset-0 z-20 bg-black/90 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">Initializing AR Mode...</p>
            <p className="text-white/70 text-sm mt-2">Loading camera and detection models</p>
          </div>
        </div>
      )}
      {/* Manual Entry Modal */}
      {manualEntry && (
        <div className="absolute inset-0 z-30 bg-black/90 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold text-foreground mb-4">Manual Product Entry</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Product Name</label>
                <input
                  type="text"
                  value={manualProduct?.name}
                  onChange={(e) => setManualProduct({ ...manualProduct, name: e?.target?.value })}
                  className="w-full px-4 py-2 rounded-lg bg-muted border border-border text-foreground"
                  placeholder="e.g., Laptop, Coffee Maker"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Estimated Cost ($)</label>
                <input
                  type="number"
                  value={manualProduct?.cost}
                  onChange={(e) => setManualProduct({ ...manualProduct, cost: e?.target?.value })}
                  className="w-full px-4 py-2 rounded-lg bg-muted border border-border text-foreground"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Category</label>
                <select
                  value={manualProduct?.category}
                  onChange={(e) => setManualProduct({ ...manualProduct, category: e?.target?.value })}
                  className="w-full px-4 py-2 rounded-lg bg-muted border border-border text-foreground"
                >
                  <option value="Shopping">Shopping</option>
                  <option value="Electronics">Electronics</option>
                  <option value="Groceries">Groceries</option>
                  <option value="Dining">Dining</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Transport">Transport</option>
                  <option value="Furniture">Furniture</option>
                </select>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => {
                    setManualEntry(false);
                    startObjectDetection();
                  }}
                  variant="outline"
                  fullWidth
                >
                  Cancel
                </Button>
                <Button onClick={handleManualSubmit} fullWidth>
                  Analyze
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Financial Impact Overlay */}
      {arActive && financialImpact && !manualEntry && (
        <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/90 to-transparent p-4">
          <div className="bg-card/95 backdrop-blur-lg rounded-2xl p-4 max-w-2xl mx-auto">
            {/* Product Info */}
            <div className={`border-2 rounded-xl p-4 mb-4 ${getRiskColorClass(financialImpact?.riskLevel)}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-bold">{detectedObject?.class}</h3>
                  <p className="text-sm opacity-80">{financialImpact?.category}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">${financialImpact?.estimatedCost}</p>
                  <p className="text-xs opacity-80">{financialImpact?.riskLevel?.toUpperCase()}</p>
                </div>
              </div>

              {/* Budget Impact */}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs opacity-80">Budget</p>
                  <p className="font-semibold">${financialImpact?.categoryBudget}</p>
                </div>
                <div>
                  <p className="text-xs opacity-80">Remaining</p>
                  <p className="font-semibold">${financialImpact?.remainingBudget}</p>
                </div>
                <div>
                  <p className="text-xs opacity-80">After Purchase</p>
                  <p className="font-semibold">${financialImpact?.afterPurchaseRemaining}</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-3">
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all duration-500"
                    style={{
                      width: `${Math.min(financialImpact?.budgetPercentage, 100)}%`,
                      backgroundColor: financialImpact?.riskColor
                    }}
                  />
                </div>
                <p className="text-xs text-center mt-1 opacity-80">
                  {financialImpact?.budgetPercentage?.toFixed(0)}% of budget used
                </p>
              </div>
            </div>

            {/* Behavioral Nudges */}
            {behavioralAnalysis && (
              <div className="space-y-2 mb-4">
                {behavioralAnalysis?.nudges?.slice(0, 2)?.map((nudge, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${getNudgeColorClass(nudge?.type)}`}
                  >
                    <p className="text-sm font-medium">{nudge?.message}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handleSimulateImpact}
                variant="outline"
                iconName="TrendingUp"
                iconPosition="left"
                fullWidth
              >
                Simulate Impact
              </Button>
              <Button
                onClick={handleManualEntry}
                variant="outline"
                iconName="Edit"
                iconPosition="left"
                fullWidth
              >
                Manual Entry
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Scenario Simulation Modal */}
      {showSimulation && scenarioSimulation && (
        <div className="absolute inset-0 z-30 bg-black/90 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-card rounded-2xl p-6 max-w-2xl w-full my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-foreground">Financial Impact Simulation</h3>
              <Button
                onClick={() => setShowSimulation(false)}
                variant="ghost"
                size="icon"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Savings Timeline */}
              <div className="p-4 bg-muted rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Target" size={20} className="text-primary" />
                  <h4 className="font-semibold text-foreground">Savings Goal Timeline</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Current Timeline</p>
                    <p className="text-2xl font-bold text-foreground">
                      {scenarioSimulation?.savingsTimeline?.currentMonthsToGoal} months
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">After Purchase</p>
                    <p className="text-2xl font-bold text-warning">
                      {scenarioSimulation?.savingsTimeline?.afterPurchaseMonthsToGoal} months
                    </p>
                  </div>
                </div>
                <p className="text-sm text-error mt-2">
                  Delay: +{scenarioSimulation?.savingsTimeline?.delayInMonths} months
                </p>
              </div>

              {/* Cash Runway */}
              <div className="p-4 bg-muted rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Clock" size={20} className="text-primary" />
                  <h4 className="font-semibold text-foreground">Cash Runway</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Current</p>
                    <p className="text-2xl font-bold text-foreground">
                      {scenarioSimulation?.cashRunway?.currentDays} days
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">After Purchase</p>
                    <p className="text-2xl font-bold text-warning">
                      {scenarioSimulation?.cashRunway?.afterPurchaseDays} days
                    </p>
                  </div>
                </div>
                <p className="text-sm text-error mt-2">
                  Reduction: -{scenarioSimulation?.cashRunway?.reductionDays} days
                </p>
              </div>

              {/* Goal Probability */}
              <div className="p-4 bg-muted rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="TrendingUp" size={20} className="text-primary" />
                  <h4 className="font-semibold text-foreground">Goal Completion Probability</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Current</p>
                    <p className="text-2xl font-bold text-success">
                      {scenarioSimulation?.goalProbability?.current}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">After Purchase</p>
                    <p className="text-2xl font-bold text-warning">
                      {scenarioSimulation?.goalProbability?.afterPurchase}%
                    </p>
                  </div>
                </div>
                <p className="text-sm text-error mt-2">
                  Change: {scenarioSimulation?.goalProbability?.change}%
                </p>
              </div>

              {/* Alternatives */}
              {behavioralAnalysis?.alternatives && behavioralAnalysis?.alternatives?.length > 0 && (
                <div className="p-4 bg-muted rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Lightbulb" size={20} className="text-warning" />
                    <h4 className="font-semibold text-foreground">Alternative Suggestions</h4>
                  </div>
                  <div className="space-y-2">
                    {behavioralAnalysis?.alternatives?.map((alt, index) => (
                      <div key={index} className="p-3 bg-card rounded-lg">
                        <p className="text-sm text-foreground">{alt?.suggestion}</p>
                        <p className="text-xs text-success mt-1">Potential savings: {alt?.savings}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={() => setShowSimulation(false)}
              fullWidth
              className="mt-6"
            >
              Close Simulation
            </Button>
          </div>
        </div>
      )}
      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-4">
        <div className="flex items-center justify-center gap-4">
          <Button
            onClick={toggleQRScanning}
            variant={qrScanEnabled ? 'default' : 'outline'}
            className={`${
              qrScanEnabled 
                ? 'bg-primary text-white border-primary' :'bg-white/20 text-white border-white/40'
            } backdrop-blur-sm`}
          >
            <Icon name="QrCode" size={20} className="mr-2" />
            {qrScanEnabled ? 'Disable QR Scan' : 'Enable QR Scan'}
          </Button>
          
          <Button
            onClick={handleManualEntry}
            variant="outline"
            className="bg-white/20 text-white border-white/40 backdrop-blur-sm"
            disabled={manualEntry}
          >
            <Icon name="Edit" size={20} className="mr-2" />
            Manual Entry
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ARModeViewer;
